import { Container } from "@mui/material";
import React, { useEffect, useState } from "react";

import {
  ActionIcon,
  Modal,
  CopyButton,
  NavLink,
  Paper,
  Text,
  Tooltip,
  Table,
  Loader,
  Divider,
  Button,
  Pagination,
  Select,
  SelectProps,
} from "@mantine/core";
import { useClickOutside, useDisclosure } from "@mantine/hooks";
import { GridRenderCellParams } from "@mui/x-data-grid";
import { IconDots, IconEye, IconHome2, IconReload } from "@tabler/icons-react";
import dayjs from "dayjs";
import { motion } from "framer-motion";

import DatagridDisplay from "../../Components/DatagridDisplay";
import MegaLoader from "../../Components/MegaLoader";

import { BrandImages, Colors, ServiceList } from "../../Lib/Data";
import { Endpoints } from "../../Lib/Endpoints";
import { NonPaginatedResponse, PaginatedResponse } from "../../Lib/Responses";
import {
  PaginationProps,
  PlanProps,
  TempProps,
  UserProfileProps,
  YoutubeTempFormProps,
} from "../../Lib/Types";
import { PerformRequest, usePerformRequest } from "../../Lib/usePerformRequest";

import "./styles.scss";
import {
  DefaultErrorNotification,
  DefaultSuccessNotification,
  DefaultWarningNotification,
  generateRandomString,
  getEllipsisFromString,
  getPaginationTotal,
} from "../../Lib/Methods";
import DisplayAlert from "../../Components/DisplayAlert";
import { useForm } from "@mantine/form";

const PendingSubscriptions: React.FC = () => {
  const [pagination, setPagination] = useState<PaginationProps>({
    page: 1,
    limit: 20,
  });
  const filterForm = useForm({
    initialValues: {
      service: "all",
    },
    validate: {
      service: (value) =>
        value && value.length > 0 ? null : "Please select a service!",
    },
  });
  const {
    data: temps,
    reloadData: reloadTemps,
    isLoading: isLoadingTemps,
  } = usePerformRequest<PaginatedResponse<TempProps[]>>({
    method: "POST",
    body: filterForm.values,
    route: Endpoints.GetUserTempsPaginated(pagination.page, pagination.limit),
  });

  const [isActionsMenuOpen, setActionsMenuOpen] = useState<boolean>(false);
  const actionsMenuRef = useClickOutside(() => setActionsMenuOpen(false));

  const [actionsMenuStyles, setActionsMenuStyles] = useState<{
    top: string;
    left: string;
  }>({
    top: "0px",
    left: "0px",
  });

  const [isTempModalOpened, { open: OpenTempModal, close: CloseTempModal }] =
    useDisclosure(false);
  const [currentUser, setCurrentUser] = useState<UserProfileProps>();

  const [currentTempForm, setCurrentTempForm] =
    useState<YoutubeTempFormProps>();
  const [currentTemp, setCurrentTemp] = useState<TempProps>();

  const [isLoadingTempForm, setLoadingTempForm] = useState<boolean>(false);
  const onContextMenu = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.preventDefault();
    setActionsMenuOpen(false);
    setActionsMenuOpen(true);

    setActionsMenuStyles({
      left: `${e.clientX.toString()}px`,
      top: `${e.clientY.toString()}px`,
    });
  };

  const [suggestedPlans, setSuggestedPlans] = useState<PlanProps[]>([]);
  const [isLoadingPlans, setLoadingPlans] = useState<boolean>(false);
  useEffect(() => {
    const FindSuggestedPlans = async () => {
      setLoadingPlans(true);
      const r = await PerformRequest<PaginatedResponse<PlanProps[]>>({
        method: "POST",
        route: Endpoints.GetPlans(1, 1),
        data: {
          service: currentTemp?.service,
          status: "active",
          isFilledSlots: false,
        },
        callback: () => {
          setLoadingPlans(false);
        },
      });
      setLoadingPlans(false);
      if (r) {
        if (r.status === 200) {
          if (r.data.data.length > 0) {
            setSuggestedPlans(r.data.data);
          } else {
            DefaultWarningNotification("There are no available plans!");
          }
        } else {
          DefaultErrorNotification("Could not get plans!");
        }
      }
    };
    const GetUserProfile = async () => {
      if (currentTemp && currentTemp.userID) {
        const r = await PerformRequest<NonPaginatedResponse<UserProfileProps>>({
          method: "GET",
          route: Endpoints.GetUserProfile(currentTemp.userID),
        });
        if (r) {
          if (r.status === 404) {
            DefaultErrorNotification("User does not exist!");
          }
          if (r.status === 200) {
            setCurrentUser(r.data.data);
          }
        }
      }
    };
    const GetTempForm = async () => {
      setCurrentTempForm(undefined);
      if (currentTemp && currentTemp.id) {
        setLoadingTempForm(true);
        const r = await PerformRequest<
          NonPaginatedResponse<YoutubeTempFormProps>
        >({
          method: "GET",
          route: Endpoints.GetYoutubeTempForm(currentTemp.id),
          callback: () => {
            setLoadingTempForm(false);
          },
        });
        setLoadingTempForm(false);
        if (r) {
          if (r.status === 404) {
            DefaultErrorNotification("Form does not exist!");
          }
          if (r.status === 200) {
            setCurrentTempForm(r.data.data);
          }
        }
      }
    };
    if (currentTemp && currentTemp.userID) {
      GetUserProfile();
      FindSuggestedPlans();
      if (currentTemp.service === "YouTube Premium") {
        GetTempForm();
      }
    }
  }, [currentTemp]);

  useEffect(() => {
    reloadTemps();
  }, [filterForm.values]);

  const [isAssigning, setAssigning] = useState<boolean>(false);

  const AssignUserToPlan = async () => {
    setAssigning(true);
    const r = await PerformRequest({
      method: "POST",
      route: Endpoints.GetAssignUserToPlan(
        currentTemp?.transactionID as string
      ),
      data: {
        userID: currentTemp?.userID,
        planID: suggestedPlans[0].id,
        service: currentTemp?.service,
        duration: currentTemp?.duration,
        tempID: currentTemp?.id,
      },
      callback: () => {
        setAssigning(false);
      },
    });
    setAssigning(false);
    if (r) {
      if (r.status === 200) {
        DefaultSuccessNotification("User assigned to plan!");
        reloadTemps();
        setCurrentTemp(undefined);
        setCurrentUser(undefined);
        CloseTempModal();
        setSuggestedPlans([]);
      } else {
        DefaultErrorNotification("An error occurred");
      }
    } else {
      DefaultErrorNotification("Could not assign user to plan!");
    }
  };
  const selectProps: SelectProps = {
    allowDeselect: false,
  };
  return (
    <Container maxWidth="lg">
      <div className="temps-container">
        <div className="component-header flex-row justify-between">
          <div className="flex-col">
            <Text className="label">Pending Subscriptions</Text>
            <Select
              w={190}
              mb={10}
              {...selectProps}
              {...filterForm.getInputProps("service")}
              label="Service"
              mr={10}
              data={[
                { value: "all", label: "All" },
                ...ServiceList.map((service) => {
                  return {
                    value: service,
                    label: service,
                  };
                }),
              ]}
            />
          </div>
          <Button
            leftSection={<IconReload size={18} />}
            onClick={reloadTemps}
            loading={isLoadingTemps}
          >
            Reload
          </Button>
        </div>
        <br />
        <motion.div
          initial={false}
          animate={{
            display: isActionsMenuOpen ? "flex" : "none",
          }}
          ref={actionsMenuRef}
        >
          <Paper
            withBorder
            style={{
              top: actionsMenuStyles.top,
              left: actionsMenuStyles.left,
              position: "absolute",
              zIndex: 99999,
            }}
            w={200}
            p={10}
          >
            <Text c="dimmed" fz="xs">
              Transaction Information
            </Text>
            <NavLink
              href="#"
              onClick={() => {
                OpenTempModal();
                setActionsMenuOpen(false);
              }}
              label="More Details"
              leftSection={<IconEye size={16} stroke={1.5} />}
              c={Colors.BluePrimary}
            />
          </Paper>
        </motion.div>

        {currentTemp && (
          <Modal
            opened={isTempModalOpened}
            onClose={CloseTempModal}
            title="Transaction Details"
            centered
            size="lg"
          >
            <Table variant="vertical" layout="fixed" withTableBorder>
              <Table.Thead>
                <Table.Tr key={generateRandomString(12)}>
                  <Table.Th w={200}>Transaction ID</Table.Th>
                  <Table.Td>
                    <CopyButton value={currentTemp.transactionID}>
                      {({ copied, copy }) => (
                        <Tooltip
                          label={copied ? "Copied!" : "Copy to clipboard"}
                        >
                          <div
                            className="pointer flex-row align-center"
                            onClick={copy}
                            style={{
                              color: copied
                                ? Colors.GreenPrimary
                                : Colors.BluePrimary,
                              height: "100%",
                            }}
                          >
                            {currentTemp.transactionID}
                          </div>
                        </Tooltip>
                      )}
                    </CopyButton>
                  </Table.Td>
                </Table.Tr>
                <Table.Tr key={generateRandomString(12)}>
                  <Table.Th>Date</Table.Th>
                  <Table.Td>
                    {dayjs(currentTemp.createdAt).format(
                      "DD MMM YYYY - hh:mmA"
                    )}
                  </Table.Td>
                </Table.Tr>
                <Table.Tr key={generateRandomString(12)}>
                  <Table.Th>Service</Table.Th>
                  <Table.Td>
                    <div className="flex-row align-center temp-service-images">
                      <img
                        src={BrandImages.retrieveImage(currentTemp.service)}
                        alt=""
                        className="image"
                      />
                    </div>
                  </Table.Td>
                </Table.Tr>
                <Table.Tr key={generateRandomString(12)}>
                  <Table.Th>User</Table.Th>
                  <Table.Td>
                    {currentUser?.name ?? <Loader size={15} />}
                  </Table.Td>
                </Table.Tr>
                <Table.Tr key={generateRandomString(12)}>
                  <Table.Th>Duration</Table.Th>
                  <Table.Td>{currentTemp.duration}</Table.Td>
                </Table.Tr>
              </Table.Thead>
            </Table>

            {currentTemp && currentTemp.service === "YouTube Premium" && (
              <>
                {isLoadingTempForm ? (
                  <MegaLoader
                    containerStyle={{
                      marginTop: 20,
                      marginBottom: 20,
                    }}
                  />
                ) : (
                  <>
                    {currentTempForm && (
                      <>
                        <Divider w="100%" mt={15} mb={10} />

                        <Table
                          variant="vertical"
                          layout="fixed"
                          withTableBorder
                        >
                          <Table.Thead>
                            <Table.Tr key={generateRandomString(12)}>
                              <Table.Th w={200}>Subscription Email</Table.Th>
                              <Table.Td>
                                <CopyButton value={currentTempForm.email}>
                                  {({ copied, copy }) => (
                                    <Tooltip
                                      label={
                                        copied ? "Copied!" : "Copy to clipboard"
                                      }
                                    >
                                      <div
                                        className="pointer flex-row align-center"
                                        onClick={copy}
                                        style={{
                                          color: copied
                                            ? Colors.GreenPrimary
                                            : Colors.BluePrimary,
                                          height: "100%",
                                        }}
                                      >
                                        {currentTempForm.email}
                                      </div>
                                    </Tooltip>
                                  )}
                                </CopyButton>
                              </Table.Td>
                            </Table.Tr>
                          </Table.Thead>
                        </Table>
                      </>
                    )}
                  </>
                )}
              </>
            )}

            <Divider w="100%" mt={15} mb={10} />
            <Text fw={500} mb={10} c={Colors.GreenSecondary}>
              Suggested Plan
            </Text>
            {suggestedPlans && suggestedPlans[0] && suggestedPlans[0].id ? (
              <>
                <Table variant="vertical" layout="fixed" withTableBorder>
                  <Table.Thead>
                    <Table.Tr key={generateRandomString(12)}>
                      <Table.Th w={200}>Plan ID</Table.Th>
                      <Table.Td>{suggestedPlans[0].id}</Table.Td>
                    </Table.Tr>
                    <Table.Tr key={generateRandomString(12)}>
                      <Table.Th w={200}>Login Email</Table.Th>
                      <Table.Td>{suggestedPlans[0]?.login?.email}</Table.Td>
                    </Table.Tr>
                    <Table.Tr key={generateRandomString(12)}>
                      <Table.Th w={200}>Available Slots</Table.Th>
                      <Table.Td>
                        {suggestedPlans[0].slots -
                          suggestedPlans[0]?.users?.length}
                      </Table.Td>
                    </Table.Tr>
                    <Table.Tr key={generateRandomString(12)}>
                      <Table.Th>Expiration Date</Table.Th>
                      <Table.Td>
                        {dayjs(suggestedPlans[0]?.expirationDate).format(
                          "DD MMM YYYY - hh:mmA"
                        )}
                      </Table.Td>
                    </Table.Tr>
                    <Table.Tr key={generateRandomString(12)}>
                      <Table.Th>Service</Table.Th>
                      <Table.Td>
                        <div className="flex-row align-center temp-service-images">
                          <img
                            src={BrandImages.retrieveImage(
                              suggestedPlans[0].service
                            )}
                            alt=""
                            className="image"
                          />
                        </div>
                      </Table.Td>
                    </Table.Tr>
                  </Table.Thead>
                </Table>
                <Button
                  onClick={AssignUserToPlan}
                  loading={isAssigning}
                  w={150}
                  mt={20}
                  color={Colors.GreenPrimary}
                >
                  Assign
                </Button>
                <Text fw={400} c={Colors.GraySecondary} fz={14}>
                  This will automatically add the user to the plan
                </Text>
              </>
            ) : (
              <>
                {isLoadingPlans ? (
                  <div className="flex-row align-center justify-center width-100">
                    <Loader my={20} size={34} />
                  </div>
                ) : (
                  <DisplayAlert
                    title="No Plans Found"
                    children={
                      <Text>
                        There are no active plans with available slots!
                      </Text>
                    }
                  />
                )}
              </>
            )}
          </Modal>
        )}

        {temps && temps.data && (
          <>
            <DatagridDisplay
              rows={temps?.data}
              isLoading={isLoadingTemps}
              onRowClick={(params) => {
                OpenTempModal();
                setCurrentTemp(params.row);
                setSuggestedPlans([]);
              }}
              columns={[
                {
                  field: "createdAt",
                  width: 190,
                  headerName: "Date",
                  renderCell: (params: GridRenderCellParams<TempProps>) => {
                    return (
                      <div
                        onContextMenu={(e) => {
                          onContextMenu(e);
                          setCurrentTemp(params.row);
                        }}
                      >
                        {dayjs(params.row.createdAt).format(
                          "DD MMM YYYY - hh:mmA"
                        )}
                      </div>
                    );
                  },
                },
                {
                  field: "transactionID",
                  flex: 1,
                  headerName: "Transaction ID",
                  renderCell: (params: GridRenderCellParams<TempProps>) => {
                    return (
                      <div
                        onContextMenu={(e) => {
                          onContextMenu(e);
                          setCurrentTemp(params.row);
                        }}
                      >
                        {params.row.transactionID}
                      </div>
                    );
                  },
                },

                {
                  field: "duration",

                  width: 130,
                  headerName: "Duration",
                  renderCell: (params: GridRenderCellParams<TempProps>) => {
                    return (
                      <div
                        onContextMenu={(e) => {
                          onContextMenu(e);
                          setCurrentTemp(params.row);
                        }}
                      >
                        {params.row.duration}
                      </div>
                    );
                  },
                },
                {
                  field: "service",
                  width: 90,
                  headerName: "Service",
                  renderCell: (params: GridRenderCellParams<TempProps>) => {
                    return (
                      <div
                        onContextMenu={(e) => {
                          onContextMenu(e);
                          setCurrentTemp(params.row);
                        }}
                        className="flex-row align-center temp-service-images"
                      >
                        <img
                          src={BrandImages.retrieveImage(params.row.service)}
                          alt=""
                          className="image"
                        />
                      </div>
                    );
                  },
                },
              ]}
            />
            <div className="flex-row align-center justify-center width-100">
              <Pagination
                value={pagination.page}
                onChange={(value) => {
                  setPagination({ ...pagination, page: value });
                }}
                my={40}
                disabled={temps?.data.length === 0 || isLoadingTemps}
                total={getPaginationTotal(
                  temps?.totalRecords,
                  pagination.limit
                )}
              />
            </div>
          </>
        )}
        {isLoadingTemps && <MegaLoader />}
      </div>
    </Container>
  );
};
export default PendingSubscriptions;
